import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import { Plus, Pencil, Trash2, Eye, EyeOff, Clock } from "lucide-react";
import { toast } from "sonner";

export default function AdminInsightsList() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api
      .get("/admin/insights")
      .then((r) => setItems(r.data || []))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!window.confirm("Delete this article? This cannot be undone.")) return;
    try {
      await api.delete(`/admin/insights/${id}`);
      toast.success("Article deleted.");
      load();
    } catch {
      toast.error("Could not delete.");
    }
  };

  return (
    <div className="p-6 md:p-10" data-testid="admin-insights-list-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <span className="bnp-chip bnp-chip-light">Insights</span>
          <h1 className="font-display text-3xl md:text-4xl text-[#04273d] mt-2 font-semibold tracking-tight">Articles</h1>
          <p className="text-sm text-[#447694] mt-1">Manage your content library. Articles published here are visible on the public Insights hub.</p>
        </div>
        <Link to="/admin/insights/new" className="btn-primary text-sm" data-testid="admin-new-article-button">
          <Plus size={14} /> New article
        </Link>
      </div>

      <div className="bnp-card mt-6 overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-[#447694] text-sm" data-testid="admin-insights-loading">Loading…</div>
        ) : items.length === 0 ? (
          <div className="p-10 text-center text-[#447694] text-sm">No articles yet.</div>
        ) : (
          <table className="w-full text-sm" data-testid="admin-insights-table">
            <thead className="bg-[#f7f7f7] text-[#447694] text-left">
              <tr>
                <th className="px-5 py-3 font-medium">Title</th>
                <th className="px-5 py-3 font-medium">Category</th>
                <th className="px-5 py-3 font-medium">Status</th>
                <th className="px-5 py-3 font-medium">Updated</th>
                <th className="px-5 py-3 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <tr key={it.id} className="border-t border-[#9ac7f7]/20" data-testid={`admin-insight-row-${it.slug}`}>
                  <td className="px-5 py-3">
                    <p className="text-[#04273d] font-medium">{it.title}</p>
                    <p className="font-mono-brand text-[10px] text-[#447694] tracking-wider mt-0.5">/{it.slug}</p>
                  </td>
                  <td className="px-5 py-3 text-[#447694]">{it.category}</td>
                  <td className="px-5 py-3">
                    {it.published ? (
                      <span className="bnp-chip bnp-chip-teal" data-testid={`admin-status-${it.slug}`}>
                        <Eye size={12} /> Published
                      </span>
                    ) : it.scheduled_publish_at ? (
                      <div className="flex flex-col gap-0.5">
                        <span className="bnp-chip bnp-chip-light border border-[#71edc9]/40" data-testid={`admin-status-${it.slug}`}>
                          <Clock size={12} /> Scheduled
                        </span>
                        <span className="font-mono-brand text-[10px] text-[#447694] tracking-wider" data-testid={`admin-scheduled-time-${it.slug}`}>
                          {new Date(it.scheduled_publish_at).toLocaleString("en-GB", {
                            dateStyle: "medium",
                            timeStyle: "short",
                          })}
                        </span>
                      </div>
                    ) : (
                      <span className="bnp-chip bnp-chip-light" data-testid={`admin-status-${it.slug}`}>
                        <EyeOff size={12} /> Draft
                      </span>
                    )}
                  </td>
                  <td className="px-5 py-3 text-[#447694] font-mono-brand text-xs">
                    {it.updated_at ? new Date(it.updated_at).toLocaleDateString("en-GB") : ","}
                  </td>
                  <td className="px-5 py-3 text-right">
                    <div className="inline-flex items-center gap-1">
                      <Link to={`/admin/insights/${it.id}/edit`} className="btn-ghost text-xs" data-testid={`admin-edit-${it.slug}`}>
                        <Pencil size={12} /> Edit
                      </Link>
                      <button onClick={() => remove(it.id)} className="btn-ghost text-xs text-red-700 hover:bg-red-50" data-testid={`admin-delete-${it.slug}`}>
                        <Trash2 size={12} /> Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
